import React from 'react'
import { Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { ordersApi } from 'api/orders'
import { ITableOrder } from 'models/orders'
import { OrderDateTime } from 'components/orders/OrderDateTime/OrderDateTime'
import { getOrderPath } from 'routing/utils/getOrderPath'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { orderTags } from 'shared/constants/orders'
import { showServerError } from 'shared/utils/showServerError'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import styles from './PromoCodeOrders.module.scss'

interface IPromoCodeOrdersProps {
  promoCodeId: string
}

const pageSize = 10

const columns: ColumnsType<ITableOrder> = [
  {
    title: 'Номер заказа',
    dataIndex: 'number'
  },
  {
    title: 'Дата и время',
    dataIndex: 'createdAt',
    render: (_, record) => <OrderDateTime value={record.createdAt} />
  },
  {
    title: 'Сумма',
    dataIndex: 'full_sum',
    render: (_, { full_sum }) => getNormalizedPrice(full_sum)
  },
  {
    title: 'Скидка',
    dataIndex: 'discount',
    render: (_, { promo_details }) =>
      getNormalizedPrice(promo_details?.discount_amount ?? 0)
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    render: (_, { status }) => (
      <Tag color={orderTags[status].color}>{orderTags[status].label}</Tag>
    )
  }
]

const mobileColumns: ColumnsType<ITableOrder> = [
  {
    title: 'Заказ',
    dataIndex: 'number',
    width: '55%',
    render: (_, record) => (
      <div className={styles.mobileCell}>
        <span className={styles.mobileNumber}>{record.number}</span>
        <span className={styles.mobileSubText}>
          <OrderDateTime value={record.createdAt} inline />
        </span>
        <span className={styles.mobileSubText}>
          Скидка: {getNormalizedPrice(record.promo_details?.discount_amount ?? 0)}
        </span>
      </div>
    )
  },
  {
    title: 'Сумма',
    dataIndex: 'full_sum',
    width: '45%',
    render: (_, { full_sum, status }) => (
      <div className={styles.mobileSumCell}>
        <Typography.Text>{getNormalizedPrice(full_sum)}</Typography.Text>
        <Tag color={orderTags[status].color}>{orderTags[status].label}</Tag>
      </div>
    )
  }
]

export const PromoCodeOrders: React.FC<IPromoCodeOrdersProps> = ({
  promoCodeId
}) => {
  const isMobile = useIsMobile()

  const [orders, setOrders] = React.useState<ITableOrder[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)

  const getOrders = async (skip: number) => {
    try {
      setIsLoading(true)
      const response = await ordersApi.getOrders({
        skip: String(skip),
        limit: String(pageSize),
        promo_code_id: promoCodeId
      })
      setTotal(response.data.count)
      setOrders(response.data.orders)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getOrders(0)
    setCurrentPage(1)
  }, [promoCodeId])

  return (
    <>
      <Typography.Title level={4} style={{ margin: '32px 0' }}>
        Заказы с этим промокодом
      </Typography.Title>
      <Table
        scroll={isMobile ? undefined : { x: 700 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={orders}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        onRow={({ _id }) => ({
          // заказ открывается в новой вкладке — форма промокода остаётся открытой
          onClick: () => window.open(getOrderPath(_id), '_blank')
        })}
        pagination={{
          current: currentPage,
          onChange: (page) => {
            getOrders((page - 1) * pageSize)
            setCurrentPage(page)
          },
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
    </>
  )
}
